import { Box, IconButton, useTheme } from "@mui/material";
import { useAtomValue, useSetAtom } from "jotai";
import { Plus, SlidersHorizontal } from "lucide-react";
import { type FC, useEffect, useState } from "react";
import { DocumentKeys } from "web_myplann/Api/Document/DocumentKeys";
import { PortfolioKeys } from "web_myplann/Api/Portfolio/portfolioKeys";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AddDocumentDrawer } from "web_myplann/Portfolio/Components/Document/AddDocumentDrawer";
import {
	SortDrawer,
	type SortKey,
	SortTypes,
} from "web_myplann/Portfolio/Components/SortDrawer";
import { DocumentListPage } from "web_myplann/Portfolio/Pages/DocumentListPage";
import { PortfolioListPage } from "web_myplann/Portfolio/Pages/PortfolioListPage";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import { MyPlannLoading } from "web_myplann/Shared/Components/MyPlannLoading";
import { MyPlannTabs } from "web_myplann/Shared/Components/MyPlannTabs";
import { NativePullToRefresh } from "web_myplann/Shared/Components/NativePullToRefresh";
import { MyPlannLayout } from "web_myplann/Shared/Header/MyPlannLayout";
import NavigationHeader from "web_myplann/Shared/Header/NavigationHeader";
import { portfolioSortState } from "web_myplann/Shared/State/portfolioSortAtom";
import { BAR_MYPLANN_LIGHT } from "web_myplann/Utils/constants";

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

const STORAGE_KEY = "portfolio-tab";

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box>{children}</Box>}
		</div>
	);
}

export const PortfolioPage: FC = () => {
	const theme = useTheme();
	const { t } = useResource();

	const [value, setValue] = useState(
		Number(localStorage.getItem(STORAGE_KEY)) || 0,
	);
	const [sorterDrawer, setSorterDrawer] = useState(false);
	const [documentDrawer, setDocumentDrawer] = useState(false);

	const { portfolioSortValue } = useAtomValue(portfolioSortState);
	const setPortfolioSortState = useSetAtom(portfolioSortState);

	const [activeSort, setActiveSort] = useState<SortKey>(portfolioSortValue);
	const [isLoading, setIsLoading] = useState(true);

	const getPullToRefreshBackgroundColor = () => {
		if (value === 0 && portfolioSortValue === SortTypes.default) {
			return theme.palette.mode === "dark"
				? theme.palette.deprecated.grey[50]
				: BAR_MYPLANN_LIGHT;
		}
		return undefined;
	};

	useEffect(() => {
		const stored = localStorage.getItem(STORAGE_KEY);
		if (stored === "1") setValue(1);
	}, []);

	const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
		setValue(newValue);
		localStorage.setItem(STORAGE_KEY, String(newValue));
	};

	return (
		<MyPlannLayout
			disableContentPadding
			topBar={
				<NavigationHeader
					title={
						value === 0
							? t(Resources.Portfolio.Titles.portfolio)
							: t(Resources.Portfolio.Titles.document)
					}
					bottomComponent={
						<MyPlannTabs
							value={value}
							onChange={handleChange}
							tabs={[
								{
									id: "portfolio",
									label: t(Resources.Portfolio.Titles.portfolio),
								},
								{
									id: "documents",
									label: t(Resources.Portfolio.Titles.document),
								},
							]}
						/>
					}
					endComponent={
						value === 0 ? (
							<IconButton onClick={() => setSorterDrawer(true)}>
								<SlidersHorizontal color={theme.palette.primary.main} />
							</IconButton>
						) : (
							<IconButton onClick={() => setDocumentDrawer(true)}>
								<Plus color={theme.palette.primary.main} />
							</IconButton>
						)
					}
					disableBottomPadding
				/>
			}
		>
			{isLoading && <MyPlannLoading />}
			<Box sx={{ display: isLoading ? "none" : "block" }}>
				<NativePullToRefresh
					queryKeys={
						value === 0
							? [
									PortfolioKeys.portfolioList(),
									PortfolioKeys.propertyList(),
									PortfolioKeys.reportingSummary(),
								]
							: [DocumentKeys.allLists]
					}
					spinnerOffset={-20}
					backgroundColor={getPullToRefreshBackgroundColor()}
				>
					<CustomTabPanel value={value} index={0}>
						<PortfolioListPage
							activeSort={activeSort}
							setIsLoading={setIsLoading}
						/>
					</CustomTabPanel>
					<CustomTabPanel value={value} index={1}>
						<DocumentListPage setIsLoading={setIsLoading} />
					</CustomTabPanel>
				</NativePullToRefresh>
			</Box>
			<MyPlannDrawer
				open={sorterDrawer}
				onClose={() => {
					setSorterDrawer(false);
				}}
				title={t(Resources.Portfolio.List.Sorter.title)}
			>
				<SortDrawer
					activeSort={activeSort}
					setSort={(val) => {
						setActiveSort(val);
						setPortfolioSortState({ portfolioSortValue: val });
						setSorterDrawer(false);
					}}
				/>
			</MyPlannDrawer>
			<MyPlannDrawer
				open={documentDrawer}
				onClose={() => setDocumentDrawer(false)}
				title={t(Resources.Portfolio.List.AddDocumentDrawer.title)}
			>
				<AddDocumentDrawer onClose={() => setDocumentDrawer(false)} />
			</MyPlannDrawer>
		</MyPlannLayout>
	);
};
